import { useEffect, useMemo, useState } from 'react';
import type { EntradaCrearUsuario, RolAsignable, Usuario } from '@copathy/shared';
import { ROLES_ASIGNABLES } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import { useGuardarUsuario } from '../lib/usuarios';
import { useClientes } from '../lib/clientes';
import { Boton } from './Boton';
import { Campo } from './Campo';
import { Modal } from './Modal';
import { Combo } from './Combo';

/**
 * Alta y edición de una cuenta.
 *
 * ── Lo que NO tiene, y es el punto ──
 * **No hay campo de contraseña.** La cuenta nace sin credencial y la persona
 * elige la suya con el enlace de la invitación. Una contraseña que otro eligió
 * tiene que viajar por correo o WhatsApp, y a partir de ahí vive en un sitio que
 * no controlamos.
 */
const NOMBRE_ROL: Record<RolAsignable, string> = {
  admin_copathy: 'Administrador COPATHY',
  consultor_copathy: 'Consultor COPATHY',
  admin_empresa: 'Administrador del cliente',
};

/**
 * A qué empresa puede ir cada rol. Es la misma regla que impone
 * `tg_usuarios_ambito_rol` en el motor; acá se refleja para no ofrecer
 * combinaciones que la base va a rechazar.
 */
const ES_DE_COPATHY: Record<RolAsignable, boolean> = {
  admin_copathy: true,
  consultor_copathy: true,
  admin_empresa: false,
};

type Formulario = { nombre: string; correo: string; rol: RolAsignable; empresaId: number | null };

export function FormularioUsuario({
  objetivo,
  copathyId,
  empresaFija,
  alCerrar,
  alFallar,
  alInvitar,
}: {
  /** `'nuevo'` para el alta, un usuario para editar, `null` cerrado. */
  objetivo: Usuario | 'nuevo' | null;
  /** Id de la fila de Copathy: es la empresa de los roles de ámbito Copathy. */
  copathyId: number | null;
  /** Si se abre desde el detalle de un cliente, la empresa viene fija. */
  empresaFija?: { id: number; nombre: string };
  alCerrar: () => void;
  alFallar: (m: string | null) => void;
  /** Recibe el enlace de dev y de quién es, para poder probar sin SMTP. */
  alInvitar?: (enlaceDev: string | undefined, nombre?: string) => void;
}) {
  const esNuevo = objetivo === 'nuevo';
  const { crear, editar } = useGuardarUsuario();
  const [datos, setDatos] = useState<Formulario>({
    nombre: '',
    correo: '',
    rol: 'admin_empresa',
    empresaId: null,
  });
  const [inicial, setInicial] = useState<Formulario | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});

  // Solo hace falta la lista de clientes cuando el rol es de empresa Y no viene
  // fija: pedirla siempre es una consulta de más en cada apertura del modal.
  const necesitaClientes = esNuevo && !empresaFija && !ES_DE_COPATHY[datos.rol];
  const clientes = useClientes('', { activo: 'si' });

  useEffect(() => {
    if (objetivo === null) return;
    const inicio: Formulario =
      objetivo === 'nuevo'
        ? {
            nombre: '',
            correo: '',
            rol: empresaFija ? 'admin_empresa' : 'admin_empresa',
            empresaId: empresaFija?.id ?? null,
          }
        : {
            nombre: objetivo.nombre,
            correo: objetivo.correo,
            rol: (ROLES_ASIGNABLES as readonly string[]).includes(objetivo.rol)
              ? (objetivo.rol as RolAsignable)
              : 'admin_empresa',
            empresaId: objetivo.empresaId,
          };
    setDatos(inicio);
    setInicial(inicio);
    setCampos({});
  }, [objetivo, empresaFija]);

  const opcionesCliente = useMemo(
    () => (clientes.data?.datos ?? []).map((c) => ({ clave: c.id, texto: c.nombre })),
    [clientes.data],
  );

  const hayCambios = inicial !== null && JSON.stringify(datos) !== JSON.stringify(inicial);
  const enviando = crear.isPending || editar.isPending;

  /*
    Un rol de Copathy va SIEMPRE a la empresa de Copathy; uno de cliente, a la que
    se elija. Resolverlo acá evita ofrecer un selector de cliente que la base va a
    rechazar de todos modos.
  */
  const empresaDestino = ES_DE_COPATHY[datos.rol] ? copathyId : datos.empresaId;

  async function guardar() {
    alFallar(null);
    setCampos({});

    if (esNuevo && !empresaDestino) {
      setCampos({ empresaId: 'Elige a qué cliente pertenece.' });
      return;
    }

    try {
      if (esNuevo) {
        const cuerpo: EntradaCrearUsuario = {
          nombre: datos.nombre.trim(),
          correo: datos.correo.trim(),
          rol: datos.rol,
          empresaId: empresaDestino!,
        };
        const r = await crear.mutateAsync(cuerpo);
        alCerrar();
        alInvitar?.(r.enlaceDev, r.usuario.nombre);
        // Sin `!== 'nuevo'`: en esta rama TypeScript ya descartó ese caso por el
        // `esNuevo` de arriba, y comparar de nuevo es una condición que no puede
        // ser falsa — de las que se quedan puestas y confunden al que lea.
      } else if (objetivo) {
        await editar.mutateAsync({
          id: objetivo.id,
          cambios: { nombre: datos.nombre.trim(), rol: datos.rol },
        });
        alCerrar();
      }
    } catch (e) {
      if (e instanceof ErrorApi) {
        if (e.campos) setCampos(e.campos);
        else alFallar(e.message);
      } else {
        alFallar('No pudimos guardar. Intenta de nuevo.');
      }
    }
  }

  return (
    <Modal
      titulo={esNuevo ? 'Crear cuenta' : 'Editar cuenta'}
      ancho="medio"
      abierto={objetivo !== null}
      hayCambios={hayCambios}
      alCerrar={alCerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton
            onClick={() => void guardar()}
            cargando={enviando}
            disabled={datos.nombre.trim().length < 3 || (esNuevo && datos.correo.trim().length < 5)}
          >
            {esNuevo ? 'Crear e invitar' : 'Guardar'}
          </Boton>
        </>
      }
    >
      <div className="space-y-4">
        <Campo
          etiqueta="Nombre completo"
          value={datos.nombre}
          onChange={(e) => setDatos((d) => ({ ...d, nombre: e.target.value }))}
          error={campos.nombre}
          autoFocus
        />

        <Campo
          etiqueta="Correo electrónico"
          type="email"
          value={datos.correo}
          onChange={(e) => setDatos((d) => ({ ...d, correo: e.target.value }))}
          error={campos.correo}
          // El correo es la identidad con la que se entra y a la que llegó la
          // invitación. Cambiarlo es dar de alta otra cuenta, no editar esta.
          disabled={!esNuevo}
        />
        {!esNuevo && (
          <p className="-mt-2 pl-5 text-xs text-marino-400">
            El correo no se cambia: es con lo que entra. Si hay que corregirlo, crea otra cuenta y
            desactiva esta.
          </p>
        )}

        <Combo
          etiqueta="Rol"
          opciones={ROLES_ASIGNABLES.map((r) => ({ clave: r, texto: NOMBRE_ROL[r] }))}
          valor={datos.rol}
          alCambiar={(v) => v !== null && setDatos((d) => ({ ...d, rol: v as RolAsignable }))}
          vacio="Elige un rol"
          error={campos.rol}
        />

        {empresaFija ? (
          <p className="rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm text-marino-600">
            La cuenta se crea para <strong className="text-marino-800">{empresaFija.nombre}</strong>.
          </p>
        ) : ES_DE_COPATHY[datos.rol] ? (
          <p className="rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm text-marino-600">
            Es un rol de COPATHY: la cuenta se crea dentro de COPATHY, no de un cliente.
          </p>
        ) : (
          <Combo
            etiqueta="Cliente"
            opciones={opcionesCliente}
            valor={datos.empresaId}
            alCambiar={(v) => setDatos((d) => ({ ...d, empresaId: v === null ? null : Number(v) }))}
            vacio={necesitaClientes && clientes.isPending ? 'Cargando…' : 'Elige el cliente'}
            error={campos.empresaId}
            sinCoincidencias="Ningún cliente coincide."
          />
        )}

        {esNuevo && (
          <p className="rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-xs text-marino-500">
            No se le pone contraseña: le llega un enlace para que elija la suya. Vence en 7 días y
            sirve una sola vez.
          </p>
        )}
      </div>
    </Modal>
  );
}
